import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  alpha,
  Badge,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Store,
  Category,
  Inventory,
  Widgets,
  Settings,
  CloudUpload,
  ChevronLeft,
  Logout,
  ExpandLess,
  ExpandMore,
  Notifications,
  DarkMode,
  LightMode,
  Search,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 280;

const menuItems = [
  { 
    text: 'Dashboard', 
    icon: <Dashboard />, 
    path: '/',
    description: 'Vista general',
  },
  { 
    text: 'Usuarios', 
    icon: <People />, 
    path: '/users',
    description: 'Gestión de usuarios',
  },
  { 
    text: 'Retails', 
    icon: <Store />, 
    path: '/retails',
    description: 'Tiendas y feeds',
  },
  { 
    text: 'Categorías', 
    icon: <Category />, 
    path: '/categories',
    description: 'Organización',
  },
  { 
    text: 'Productos', 
    icon: <Inventory />, 
    path: '/products',
    description: 'Catálogo completo',
  },
  { 
    text: 'Widgets', 
    icon: <Widgets />, 
    path: '/widgets',
    description: 'Configuración de upselling',
  },
  { 
    text: 'Importar Specs', 
    icon: <CloudUpload />, 
    path: '/specs/import',
    description: 'Especificaciones técnicas',
  },
  { 
    text: 'Configuración', 
    icon: <Settings />, 
    path: '/config',
    description: 'Ajustes del sistema',
  },
];

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const currentDrawerWidth = collapsed && !isMobile ? 80 : drawerWidth;

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed && !isMobile ? 'center' : 'space-between',
          px: collapsed && !isMobile ? 1 : 3,
          py: 2.5,
          minHeight: 72,
        }}
      >
        {(!collapsed || isMobile) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src="/logo-light.png"
              alt="Omnitok"
              sx={{
                height: 32,
              }}
            />
            <Box>
              <Typography
                variant="caption"
                sx={{ 
                  color: '#7c8af8', 
                  fontWeight: 600,
                  lineHeight: 1,
                  display: 'block',
                }}
              >
                Upselling
              </Typography>
            </Box>
          </Box>
        )}
        {collapsed && !isMobile && (
          <Box
            component="img"
            src="/logo-light.png"
            alt="Omnitok"
            sx={{
              height: 28,
              maxWidth: 50,
              objectFit: 'contain',
            }}
          />
        )}
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <ChevronLeft />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ mx: 2 }} />

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 2, overflow: 'auto' }}>
        <List sx={{ px: 1.5 }}>
          {menuItems
            .filter((item) => !item.adminOnly || isAdmin)
            .map((item) => {
              const active = isActive(item.path);
              return (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                  <Tooltip
                    title={collapsed && !isMobile ? item.text : ''}
                    placement="right"
                    arrow
                  >
                    <ListItemButton
                      selected={active}
                      onClick={() => handleMenuClick(item.path)}
                      sx={{
                        borderRadius: 2,
                        minHeight: 48,
                        justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                        px: collapsed && !isMobile ? 1.5 : 2,
                        transition: 'all 0.2s ease-in-out',
                        '&.Mui-selected': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 4,
                            height: '60%',
                            borderRadius: '0 4px 4px 0',
                            backgroundColor: 'primary.main',
                          },
                        },
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed && !isMobile ? 0 : 44,
                          color: active ? 'primary.main' : 'text.secondary',
                          justifyContent: 'center',
                        }}
                      >
                        {React.cloneElement(item.icon, { 
                          sx: { 
                            fontSize: 22,
                            transition: 'transform 0.2s ease-in-out',
                          } 
                        })}
                      </ListItemIcon>
                      {(!collapsed || isMobile) && (
                        <ListItemText
                          primary={item.text}
                          secondary={!isMobile ? item.description : null}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: active ? 600 : 500,
                            color: active ? 'primary.main' : 'text.primary',
                          }}
                          secondaryTypographyProps={{
                            fontSize: '0.7rem',
                            sx: { mt: -0.25 },
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
        </List>
      </Box>

      {/* User Info Section */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: (theme) => alpha(theme.palette.grey[100], 0.5),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: collapsed && !isMobile ? 0 : 1,
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
            },
          }}
          onClick={handleProfileMenuOpen}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(139, 92, 246, 0.4)',
            }}
          >
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          {(!collapsed || isMobile) && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                Administrador
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          backgroundColor: alpha('#ffffff', 0.8),
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          transition: 'width 0.2s ease-in-out, margin-left 0.2s ease-in-out',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Collapse button for desktop */}
          <IconButton
            onClick={() => setCollapsed(!collapsed)}
            sx={{ 
              display: { xs: 'none', md: 'flex' },
              color: 'text.secondary',
            }}
          >
            {collapsed ? <MenuIcon /> : <ChevronLeft />}
          </IconButton>

          <Box sx={{ flex: 1 }} />

          {/* Search button */}
          <IconButton sx={{ color: 'text.secondary' }}>
            <Search />
          </IconButton>

          {/* Notifications */}
          <IconButton sx={{ color: 'text.secondary' }}>
            <Badge badgeContent={3} color="error" variant="dot">
              <Notifications />
            </Badge>
          </IconButton>

          {/* User Menu */}
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ p: 0.5 }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: 2,
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <MenuItem
              onClick={handleLogout}
              sx={{
                mt: 1,
                mx: 1,
                borderRadius: 1,
                color: 'error.main',
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              <ListItemIcon sx={{ color: 'error.main' }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Cerrar Sesión" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{
          width: { md: currentDrawerWidth },
          flexShrink: { md: 0 },
          transition: 'width 0.2s ease-in-out',
        }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              border: 'none',
              boxShadow: '1px 0 10px rgba(0,0,0,0.05)',
              transition: 'width 0.2s ease-in-out',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          transition: 'margin-left 0.2s ease-in-out',
        }}
      >
        <Toolbar />
        <Box
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            maxWidth: 1600,
            mx: 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
